import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { createTheme } from '@mui/material/styles';

import { DashboardOutlined, PolicyOutlined, SensorDoorOutlined, FingerprintOutlined, AssuredWorkloadOutlined, LogoutOutlined, PermIdentityOutlined, PersonPinOutlined, CameraAltOutlined } from '@mui/icons-material';

import { AppProvider } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { useDemoRouter } from '@toolpad/core/internal';
import EIMP from '../optum-page/EIMP';
import EIMPData from '../optum-page/EIMP-Data';

import IdVerse from '../optum-page/idverse/IdVerse';

import ThreatMetrix from '../optum-page/threatmetrix/ThreatMetrix';
import HSIDIDProofing from '../optum-page/hsid-id-proofing/HSIDIDProofing';
import { TabProvider } from './TabProvider';
import Clearme from '../optum-page/clearme/Clearme';
import Logout from '../login/Logout';
import OnfidoImpl from '../optum-page/onfido/OnfidoImpl';
import HomePageComponent from './HomeComponent';
import OTPProgressPage from '../otp-progress/OTPProgressPage';

const ADMIN_NAVIGATION = [
  {
    segment: 'home',
    title: 'User Info',
    icon: <DashboardOutlined />,
  },
  {
    segment: 'otp-progress',
    title: 'Identity Proofing',
    icon: <FingerprintOutlined />,
  },
  {
    segment: 'eimp',
    title: 'EIMP',
    icon: <PolicyOutlined />,
  },
  {
    segment: 'eimp-data',
    title: 'EIMP Data',
    icon: <PolicyOutlined />,
  },
  {
    segment: 'id-proofing',
    title: 'ID Proofing',
    icon: <SensorDoorOutlined />,
  },
  {
    segment: 'theratmetrix',
    title: 'Threatmetrix',
    icon: <CameraAltOutlined />,
  },
  {
    segment: 'clearme',
    title: 'Vendor 1',
    icon: <AssuredWorkloadOutlined />,
  },  
  {
    segment: 'idverse',
    title: 'IDVerse',
    icon: <PersonPinOutlined />,
  },
  {
    segment: 'onfido',
    title: 'Vendor 2',
    icon: <PermIdentityOutlined />,
  },  
  {
    segment: 'logout',
    title: 'Logout',
    icon: <LogoutOutlined />,
  },
];

const USER_NAVIGATION = [

  {
    segment: 'home',
    title: 'User Info',
    icon: <DashboardOutlined />,
  },
  {
    segment: 'otp-progress',
    title: 'Identity Proofing',
    icon: <FingerprintOutlined />,
  },
  {
    segment: 'idverse',
    title: 'IDVerse',
    icon: <PersonPinOutlined />,
  },
  {
    segment: 'logout',
    title: 'Logout',
    icon: <LogoutOutlined />,
  },
];

function NotFound() {
  return <h1>404: Page Not Found</h1>;
}

const customTheme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'data-toolpad-color-scheme',
  },
  colorSchemes: {
    light: {
      palette: {
        background: {
          default: '#F9F9FE',
          paper: '#EEEEF9',
        },
      },
    },
    dark: {
      palette: {
        background: {
          default: '#2A4364',
          paper: '#112E4D',
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 600,
      lg: 1200,
      xl: 1536,
    },
  },
});

function POCPageContent({ pathname, projects, isAdmin, threatMetrixScriptUrl }) {

  let component = null;
  switch (pathname) {
    case '/home':
      component = <HomePageComponent />;
      break;
    case '/otp-progress':
      component = <OTPProgressPage threatMetrixScriptUrl={threatMetrixScriptUrl} />;
      break;
    case '/eimp':
      component = <EIMP />;
      break;
    case '/eimp-data':
      component = <EIMPData />;
      break;
    case '/id-proofing':
      component = <HSIDIDProofing />;
      break;
    case '/theratmetrix':
      component = <ThreatMetrix />;
      break;
    case '/clearme':
      component = <Clearme projects={projects} isAdmin={isAdmin}/>;
      break;
    case '/onfido':
        component = <OnfidoImpl />;
        break;
    case '/idverse':
      component = <IdVerse isAdmin={isAdmin}/>;
      break
    case '/logout':
      component = <Logout />;
      break;
    default:
      component = <NotFound />;
      break;
  }

  return (
    <Box
      sx={{
        py: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      {component}
    </Box>
  );
}

POCPageContent.propTypes = {
  pathname: PropTypes.string.isRequired,
  projects: PropTypes.array,
  isAdmin: PropTypes.bool,
  threatMetrixScriptUrl: PropTypes.string,
};

function HomePage(props) {
  const { window } = props;
  let scriptLoadInitiated = false;
  const [projects, setProjects] = React.useState([]);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [isHarnessUser, setIsHarnessUser] = React.useState(false);
  const [userInformation, setUserInformation] = React.useState("");
  const [threatMetrixScriptUrl, setThreatMetrixScriptUrl] = React.useState('');
  React.useEffect(() => {

    const fetchThreatmetrixData = async () => {
      if (scriptLoadInitiated) {
        return;
      } 
      scriptLoadInitiated = true;
      try {
        const response = await fetch('/api/init');
        const data = await response.json();
        const { SessionID, OrgID, Projects, isAdminUser, Account,  harnessUser} = data;
        
        setIsAdmin(isAdminUser);
        setProjects(Projects);
        setIsHarnessUser(harnessUser);
        setUserInformation(Account?.name);
        // Call the ThreatMetrix script with the session ID and org ID
        const scriptUrl = `https://h.online-metrix.net/fp/tags.js?org_id=${OrgID}&session_id=${SessionID}`;
        setThreatMetrixScriptUrl(scriptUrl);
        const script = document.createElement('script');
        script.src = scriptUrl;
        document.body.appendChild(script);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchThreatmetrixData();
  }, []);

  const router = useDemoRouter('/home');

  const NAVIGATION = (isAdmin && !isHarnessUser) ? ADMIN_NAVIGATION : USER_NAVIGATION

  return (
    <TabProvider>
      <AppProvider
        navigation={ [ {
            kind: 'header',
            title: userInformation,
          }, ...NAVIGATION]}
        branding={{
          logo: <img src="https://www.optum.com/content/dam/optum4/images/logos/optum-logo-ora-rgb1.svg" alt="optum" />,
          title: 'Optum',
        }}
        router={router}
        theme={customTheme}
        window={window}
      >
        <DashboardLayout>
          <POCPageContent
            pathname={router.pathname}
            projects={projects}
            isAdmin={isAdmin && !isHarnessUser}
            threatMetrixScriptUrl={threatMetrixScriptUrl}
          />
        </DashboardLayout>
      </AppProvider>
    </TabProvider>
  );
}

export default HomePage;